import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { toast } from "sonner";
import { Award, Sparkles, Trophy, X, ShieldAlert, ShieldCheck, Gauge, Loader2 } from "lucide-react";

const SUGGESTED = [
  // Tech
  "Python", "JavaScript", "React", "SQL", "AWS", "AutoCAD",
  // Office & business
  "Excel", "Project Management", "Digital Marketing", "Accounting",
  "Finance", "Sales", "Customer Service", "Human Resources", "Supply Chain",
  // Languages
  "Arabic", "English", "German", "French",
  // Popular general jobs in KSA
  "Teacher", "Nurse", "Pharmacist", "Electrician", "Mechanic",
  "Chef", "Barista", "Hairdresser", "Graphic Design", "Photography",
  "Content Writing", "Translation", "Video Editing",
  // Trades / services
  "Driver", "Security Guard", "Warehouse", "Construction", "Plumbing",
  "HVAC", "Welding", "Real Estate", "Hospitality", "Tourism",
];

const TIER_STYLE = {
  Platinum: { color: "text-[#8b5cf6]", bg: "bg-[#8b5cf6]/10", border: "border-[#8b5cf6]", label: "ELITE" },
  Diamond:  { color: "text-[#06b6d4]", bg: "bg-[#06b6d4]/10", border: "border-[#06b6d4]", label: "EXPERT" },
  Gold:     { color: "text-[#F4B400]", bg: "bg-[#F4B400]/10", border: "border-[#F4B400]", label: "ADVANCED" },
  Silver:   { color: "text-[#9AA0A6]", bg: "bg-[#9AA0A6]/10", border: "border-[#9AA0A6]", label: "PROFICIENT" },
  Bronze:   { color: "text-[#CD7F32]", bg: "bg-[#CD7F32]/10", border: "border-[#CD7F32]", label: "COMPETENT" },
};

export default function Skills() {
  const { t } = useI18n();
  const [badges, setBadges] = useState([]);
  const [customSkill, setCustomSkill] = useState("");
  const [difficulty, setDifficulty] = useState("hard");
  const [starting, setStarting] = useState(false);
  const [test, setTest] = useState(null);
  const [answers, setAnswers] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  // Anti-cheat tracking
  const focusLostRef = useRef(0);
  const pasteCountRef = useRef(0);
  const testStartRef = useRef(null);
  const answerTimesRef = useRef([]); // seconds per question
  const lastAnswerAtRef = useRef(null);

  const load = () => api.get("/skills/badges").then((r) => setBadges(r.data)).catch(() => {});

  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (!test || result) return;
    const onBlur = () => { focusLostRef.current += 1; };
    const onPaste = () => { pasteCountRef.current += 1; };
    window.addEventListener("blur", onBlur);
    window.addEventListener("paste", onPaste);
    return () => {
      window.removeEventListener("blur", onBlur);
      window.removeEventListener("paste", onPaste);
    };
  }, [test, result]);

  // Track which skill tile is currently generating a quiz, so we can
  // show a per-tile spinner (the user was confused there was no feedback).
  const [generatingFor, setGeneratingFor] = useState(null);

  const start = async (skill) => {
    if (!skill.trim()) return;
    setStarting(true);
    setGeneratingFor(skill);
    setResult(null);
    try {
      const r = await api.post("/skills/start", { skill, difficulty });
      setTest(r.data);
      setAnswers(new Array(r.data.questions.length).fill(-1));
      setCustomSkill("");
      focusLostRef.current = 0;
      pasteCountRef.current = 0;
      testStartRef.current = Date.now();
      answerTimesRef.current = [];
      lastAnswerAtRef.current = Date.now();
    } catch {
      toast.error("Could not start test");
    } finally {
      setStarting(false);
      setGeneratingFor(null);
    }
  };

  const pick = (qi, oi) => {
    const now = Date.now();
    const secSince = (now - (lastAnswerAtRef.current || now)) / 1000;
    answerTimesRef.current[qi] = secSince;
    lastAnswerAtRef.current = now;
    setAnswers((prev) => {
      const next = [...prev];
      next[qi] = oi;
      return next;
    });
  };

  const submit = async () => {
    if (answers.some((a) => a < 0)) {
      toast.error("Answer every question");
      return;
    }
    setSubmitting(true);
    const times = answerTimesRef.current.filter((x) => x !== undefined && x > 0);
    const totalSec = (Date.now() - (testStartRef.current || Date.now())) / 1000;
    const integrity = {
      total_time_sec: totalSec,
      focus_lost_count: focusLostRef.current,
      min_answer_time_sec: times.length ? Math.min(...times) : 0,
      avg_answer_time_sec: times.length ? times.reduce((a, b) => a + b, 0) / times.length : 0,
      suspicious_paste_count: pasteCountRef.current,
    };
    try {
      const r = await api.post("/skills/submit", { test_id: test.test_id, answers, integrity });
      setResult(r.data);
      if (r.data.passed) {
        toast.success(`${r.data.badge.tier} badge earned — ${r.data.score}%`);
      } else {
        toast(`Scored ${r.data.score}% — keep pushing to earn a badge`);
      }
      load();
    } catch {
      toast.error("Submit failed");
    } finally {
      setSubmitting(false);
    }
  };

  const close = () => {
    setTest(null);
    setAnswers([]);
    setResult(null);
  };

  return (
    <div className="space-y-10 max-w-4xl" data-testid="skills-page">
      <div>
        <div className="font-mono text-xs uppercase tracking-[0.2em] text-gray-500 mb-3">/ Verified Skills</div>
        <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter">
          Prove what you know.
          <br />
          <span className="text-gradient">Recruiters see your badge.</span>
        </h1>
        <p className="text-white/70 mt-3 max-w-2xl">
          Proctored, anti-cheat skill assessments. 5 badge tiers — <span className="font-bold text-[#8b5cf6]">Platinum</span>{" "}
          means you smashed a university-level exam with perfect integrity. Partner companies filter candidates by
          tier when hiring.
        </p>
      </div>

      {/* Tier legend */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-0 border border-black/10">
        {["Platinum", "Diamond", "Gold", "Silver", "Bronze"].map((tier) => {
          const s = TIER_STYLE[tier];
          const thresholds = { Platinum: "98%+", Diamond: "92%+", Gold: "85%+", Silver: "75%+", Bronze: "60%+" };
          const hard = { Platinum: "Hard only", Diamond: "Hard / Normal", Gold: "Any", Silver: "Any", Bronze: "Any" };
          return (
            <div key={tier} className="p-4 border-e border-black/10 last:border-e-0">
              <Award className={`w-7 h-7 mb-2 ${s.color}`} />
              <div className="font-display font-bold">{tier}</div>
              <div className="font-mono text-[10px] text-gray-500 uppercase tracking-widest">{s.label}</div>
              <div className="text-xs text-gray-600 mt-2">{thresholds[tier]}</div>
              <div className="text-[10px] text-gray-500 font-mono mt-1">{hard[tier]}</div>
            </div>
          );
        })}
      </div>

      {/* Earned badges */}
      <section>
        <h2 className="font-display font-bold text-xl mb-4 flex items-center gap-2">
          <Trophy className="w-5 h-5" /> Your badges
        </h2>
        {badges.length === 0 ? (
          <div className="border border-dashed border-black/20 p-10 text-center text-gray-500 text-sm">
            No badges yet — earn your first below.
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4" data-testid="badges-list">
            {badges.map((b) => {
              const s = TIER_STYLE[b.tier] || TIER_STYLE.Bronze;
              return (
                <div key={b.id} className={`border ${s.border} ${s.bg} p-5 flex items-center gap-4`}>
                  <Award className={`w-10 h-10 ${s.color}`} />
                  <div className="flex-1 min-w-0">
                    <div className="font-display font-bold text-lg truncate">{b.skill}</div>
                    <div className="text-xs text-gray-500 font-mono uppercase tracking-widest">
                      {b.tier} · {b.score}%
                      {b.difficulty === "hard" && <span className="ms-2 text-[#8b5cf6]">HARD</span>}
                    </div>
                    {b.integrity_score < 100 && (
                      <div className="text-[10px] text-gray-500 font-mono mt-1">Integrity: {b.integrity_score}/100</div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* Take test */}
      <section>
        <h2 className="font-display font-bold text-xl mb-4">Take a new test</h2>

        <div className="mb-4 flex gap-2">
          {[
            { v: "easy", label: "Easy", sub: "Intro" },
            { v: "normal", label: "Normal", sub: "Intermediate" },
            { v: "hard", label: "Hard", sub: "University / Elite · Platinum eligible" },
          ].map((d) => (
            <button
              key={d.v}
              onClick={() => setDifficulty(d.v)}
              className={`px-4 py-3 text-sm font-medium border transition-colors ${
                difficulty === d.v
                  ? "bg-white text-black border-white"
                  : "bg-transparent text-white border-white/20 hover:border-white"
              }`}
              data-testid={`diff-${d.v}`}
            >
              <div className="font-bold">{d.label}</div>
              <div className="text-[10px] font-mono opacity-70">{d.sub}</div>
            </button>
          ))}
        </div>

        <div className="flex flex-wrap gap-2 mb-4" data-testid="skills-suggested">
          {SUGGESTED.map((s) => {
            const isLoading = generatingFor === s;
            return (
              <button
                key={s}
                onClick={() => start(s)}
                disabled={starting}
                className={`px-4 py-2 border text-sm font-medium transition-colors disabled:cursor-not-allowed flex items-center gap-2 ${
                  isLoading
                    ? "border-white bg-white text-black"
                    : starting
                    ? "border-white/20 opacity-40"
                    : "border-white/20 hover:bg-white hover:text-black"
                }`}
                data-testid={`skill-${s}`}
              >
                {isLoading && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                {s}
              </button>
            );
          })}
        </div>
        <div className="flex gap-3 max-w-md">
          <input
            value={customSkill}
            onChange={(e) => setCustomSkill(e.target.value)}
            placeholder="Or type any skill…"
            className="flex-1 bg-transparent border-b-2 border-white/15 focus:border-white outline-none py-3 text-white placeholder-white/40"
            data-testid="skill-custom-input"
          />
          <button
            onClick={() => start(customSkill)}
            disabled={starting || !customSkill.trim()}
            className="bg-white text-black hover:bg-white/85 font-bold px-6 py-3 flex items-center gap-2 transition-colors disabled:opacity-50"
            data-testid="skill-custom-start"
          >
            {starting && generatingFor === customSkill ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Sparkles className="w-4 h-4" />
            )}
            Test
          </button>
        </div>
      </section>

      {/* Full-screen loading overlay while quiz is being generated */}
      {starting && !test && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-[#0a0a0b]/95 backdrop-blur-sm p-4"
          data-testid="skill-generating-overlay"
        >
          <div className="bg-[#141416] border border-white/10 p-10 max-w-md w-full text-center space-y-5">
            <Loader2 className="w-12 h-12 animate-spin text-white mx-auto" />
            <div>
              <div className="font-display font-black text-2xl mb-2 text-white">
                Generating your {generatingFor || "quiz"}…
              </div>
              <div className="text-sm text-white/70 leading-relaxed">
                Claude is drafting {difficulty === "hard" ? "university-level" : difficulty === "normal" ? "intermediate" : "intro"} questions
                from scratch. This can take <b className="text-white">10–60 seconds</b>.
              </div>
            </div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-white/40">
              Please don't refresh the page.
            </div>
          </div>
        </div>
      )}

      {/* Test modal — full-opaque dark panel so questions stay readable */}
      {test && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-sm p-4" data-testid="skill-test-modal">
          <div className="bg-[#141416] w-full max-w-2xl border border-white/10 max-h-[92vh] flex flex-col shadow-2xl">
            <div className="flex items-center justify-between p-5 border-b border-white/10 bg-white text-black">
              <div>
                <div className="font-mono text-xs uppercase tracking-widest text-black/60">
                  Proctored · {test.difficulty?.toUpperCase()}
                </div>
                <h3 className="font-display font-bold text-2xl">{test.skill}</h3>
              </div>
              <button onClick={close} className="p-1 hover:bg-black/10" data-testid="skill-modal-close">
                <X className="w-5 h-5" />
              </button>
            </div>

            {!result && (
              <div className="px-5 py-2 border-b border-white/10 bg-[#3d3418] text-xs font-mono text-[#FFD24C] flex items-center gap-2">
                <ShieldAlert className="w-3.5 h-3.5" />
                Do not switch tabs or paste answers. Unusual activity lowers your integrity score.
              </div>
            )}

            {!result ? (
              <>
                <div className="flex-1 overflow-y-auto p-6 space-y-6 bg-[#141416]" data-testid="skill-questions">
                  {test.questions.map((q, qi) => (
                    <div key={qi}>
                      <div className="font-display font-bold mb-3 text-white leading-snug">
                        <span className="font-mono text-white/50 me-2">{String(qi + 1).padStart(2, "0")}</span>
                        {q.q}
                      </div>
                      <div className="space-y-2">
                        {q.options.map((opt, oi) => {
                          const active = answers[qi] === oi;
                          return (
                            <button
                              key={oi}
                              onClick={() => pick(qi, oi)}
                              className={`w-full text-start p-3 border text-sm transition-colors ${
                                active
                                  ? "bg-white text-black border-white"
                                  : "bg-[#1a1a1c] text-white border-white/15 hover:border-white"
                              }`}
                              data-testid={`q${qi}-opt${oi}`}
                            >
                              <span className="font-mono me-3 opacity-70">{String.fromCharCode(65 + oi)}</span>
                              {opt}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="border-t border-white/10 p-4 flex justify-between items-center bg-[#141416]">
                  <div className="text-sm text-white/60">
                    {test.difficulty === "hard" ? "Need 98% for Platinum · 92% for Diamond" : "Pass 60%+ to earn a badge"}
                  </div>
                  <button
                    onClick={submit}
                    disabled={submitting}
                    className="bg-white text-black hover:bg-white/85 font-bold px-6 py-3 disabled:opacity-50 inline-flex items-center gap-2"
                    data-testid="skill-submit"
                  >
                    {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                    {submitting ? "Grading…" : "Submit"}
                  </button>
                </div>
              </>
            ) : (
              <div className="p-8 text-center space-y-6 overflow-y-auto bg-[#141416] text-white">
                <div className={`num-ticker font-display font-black text-8xl tracking-tighter ${result.passed ? "text-[#34C759]" : "text-[#FF3B30]"}`}>
                  {result.score}%
                </div>
                <div className="text-lg text-white/80">
                  {result.correct} / {result.total} correct
                </div>
                {result.badge ? (
                  <div className={`inline-flex items-center gap-3 ${TIER_STYLE[result.badge.tier].bg} border ${TIER_STYLE[result.badge.tier].border} p-5`}>
                    <Award className={`w-12 h-12 ${TIER_STYLE[result.badge.tier].color}`} />
                    <div className="text-start">
                      <div className="font-mono text-xs uppercase tracking-widest text-white/60">Badge earned</div>
                      <div className="font-display font-black text-2xl">{result.badge.tier}</div>
                      <div className="text-xs text-white/70">{test.skill} · {result.difficulty.toUpperCase()}</div>
                    </div>
                  </div>
                ) : (
                  <div className="text-white/70 text-sm">No badge this time — try again to hit the minimum tier.</div>
                )}
                {/* Integrity panel */}
                <div className={`text-start border p-4 ${result.integrity.likely_cheated ? "border-[#FF3B30] bg-[#FF3B30]/10" : "border-white/10 bg-white/5"}`}>
                  <div className="flex items-center gap-2 mb-2">
                    {result.integrity.likely_cheated ? (
                      <ShieldAlert className="w-4 h-4 text-[#FF3B30]" />
                    ) : (
                      <ShieldCheck className="w-4 h-4 text-[#34C759]" />
                    )}
                    <div className="font-mono text-xs uppercase tracking-widest text-white/60">Integrity score</div>
                    <div className="ms-auto flex items-center gap-1">
                      <Gauge className="w-3.5 h-3.5" />
                      <span className="num-ticker font-bold">{result.integrity.score}/100</span>
                    </div>
                  </div>
                  {result.integrity.flags.length === 0 ? (
                    <div className="text-xs text-white/70">No anomalies detected. Clean run.</div>
                  ) : (
                    <ul className="text-xs text-white/80 space-y-1">
                      {result.integrity.flags.map((f, i) => (
                        <li key={i} className="flex gap-2"><span className="text-[#FF3B30]">▸</span>{f}</li>
                      ))}
                    </ul>
                  )}
                </div>
                <button onClick={close} className="bg-white text-black hover:bg-white/85 font-bold px-8 py-3" data-testid="skill-close">
                  Done
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
